import ResponsiveContainer from '@commons/containers/ResponsiveContainer';
import PageBuilder from '@commons/pagebuilder';
import type { DrupalSection } from '@commons/types/drupal';
import type { Alignment, Spacing, Theme, Width } from '@commons/types/styles';

import { Background, type BackgroundSettings } from '../Background';
import { type SectionMinHeightType, getSectionMinHeight } from '../helpers';
import Region from './Region';

// Type definition for the BasicSection component properties.

type Props = {
  appearance: BackgroundSettings & {
    theme: Theme;
  };
  container: {
    contentWidth: Width;
    paddingTop: Spacing;
    paddingBottom: Spacing;
    horizontalAlignment: Alignment;
    verticalAlignment: Alignment;
    minHeightType: SectionMinHeightType;
    customMinHeight?: string;
    fluid: boolean;
  };
  layout: {
    horizontalAlignment: Alignment;
  };
};

/**
 * Basic section component.
 *
 * IMPORTANT:
 * - This component should be a server component, otherwise everything it renders and itself will be client components.
 *   Since it is used on almost every pages, this would bloat the JavaScript bundle significantly.
 */

const BasicSection = ({ type, blocks, weight, settings }: DrupalSection<Props>) => {
  const { appearance, container, layout } = settings;

  const {
    contentWidth,
    paddingTop,
    paddingBottom,
    horizontalAlignment,
    verticalAlignment,
    minHeightType,
    customMinHeight,
    fluid,
  } = container;

  const minHeight = getSectionMinHeight(minHeightType, customMinHeight);

  const sortedBlocks = PageBuilder.sortBlocksByWeight(blocks);
  const renderedBlocks = sortedBlocks.map(PageBuilder.renderBlock);

  const renderedRegion = PageBuilder.renderRegion(
    'content',
    Region,
    {
      align: layout.horizontalAlignment,
      width: { xs: 12, md: contentWidth },
      children: renderedBlocks,
    },
    { type, weight }
  );

  const isPriority = weight === 0;
  const renderedBackground = <Background priority={isPriority} {...appearance} />;

  return (
    <ResponsiveContainer
      fluid={fluid}
      theme={appearance.theme}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      horizontalAlignment={horizontalAlignment}
      verticalAlignment={verticalAlignment}
      outsideChildren={renderedBackground}
      style={{ minHeight: minHeight }}
    >
      {renderedRegion}
    </ResponsiveContainer>
  );
};

export default BasicSection;
